import { useState, useEffect } from 'react'
import { db } from '../lib/supabase'
import { scoreDCI, scoreIPIP, computeMSScore } from '../lib/scoringEngine'

/**
 * Hook to fetch couple data and compute all scoring analysis.
 * @param {string} respondentId - Either partner's respondent ID
 * @returns {{ person1, person2, msScores, loading, error }}
 */
export default function useCoupleAnalysis(respondentId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!respondentId) return

    let cancelled = false

    async function fetchAndScore() {
      try {
        setLoading(true)
        setError(null)

        const coupleData = await db.getCoupleData(respondentId)

        if (cancelled) return

        // Build likert response maps (questionId → score)
        const buildLikertMap = (likertResponses) => {
          const map = {}
          for (const r of likertResponses) {
            map[r.question_id] = r.score
          }
          return map
        }

        // Extract gender and age from profile responses
        const getProfile = (profileResponses) => {
          const map = {}
          for (const r of profileResponses) {
            map[r.question_id] = r.answer
          }
          const genderMap = { Male: 'M', Female: 'F' }
          const gender = genderMap[map.q3]
          if (!gender) {
            throw new Error(`Unknown biological sex value: "${map.q3}". Expected "Male" or "Female".`)
          }
          const age = parseInt(map.q4)
          if (!age || age < 1 || age > 120) {
            throw new Error(`Invalid age value: "${map.q4}". Expected a number between 1 and 120.`)
          }
          return {
            gender,
            genderLabel: map.q3,
            age,
            knownPartner: map.q7 || '\u2014',
            exclusiveRelationship: map.q8 || '\u2014',
            relationshipStatus: map.q9 || '\u2014',
            previouslyDivorced: map.q10 || '\u2014',
            viewOfMarriage: map.q11 || '\u2014',
            healthIssues: map.q12 || '\u2014',
            stressors: map.q13 || '\u2014',
          }
        }

        const p1Likert = buildLikertMap(coupleData.person1.likertResponses)
        const p2Likert = buildLikertMap(coupleData.person2.likertResponses)
        const p1Profile = getProfile(coupleData.person1.profileResponses)
        const p2Profile = getProfile(coupleData.person2.profileResponses)

        // Run scoring
        const p1DCI = scoreDCI(p1Likert)
        const p2DCI = scoreDCI(p2Likert)
        const p1IPIP = scoreIPIP(p1Likert, p1Profile.gender, p1Profile.age)
        const p2IPIP = scoreIPIP(p2Likert, p2Profile.gender, p2Profile.age)
        const msScores = computeMSScore(p1IPIP, p2IPIP, p1DCI, p2DCI)

        if (cancelled) return

        setData({
          person1: {
            respondent: coupleData.person1.respondent,
            profile: p1Profile,
            dci: p1DCI,
            ipip: p1IPIP,
          },
          person2: {
            respondent: coupleData.person2.respondent,
            profile: p2Profile,
            dci: p2DCI,
            ipip: p2IPIP,
          },
          msScores,
        })
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load couple analysis')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchAndScore()
    return () => { cancelled = true }
  }, [respondentId])

  return {
    person1: data?.person1 ?? null,
    person2: data?.person2 ?? null,
    msScores: data?.msScores ?? null,
    loading,
    error,
  }
}
